/** Header-action builder for the Resource Library view.
 *
 *  Three modes, resolved in this order:
 *    1. rate-item form open  → back · + Resource · Save
 *    2. merchant catalogue   → search · Filter · Sort · image toggle
 *    3. rate-item list       → search · collapse · rate profile
 *
 *  Modes 2 and 3 arrive on the same `resource-library:state-change` event,
 *  discriminated by `kind`, because the two panes are mutually exclusive —
 *  ResourceLibraryManagement renders one or the other, never both.
 *
 *  No JSX: every action fires a window event the pane listens for. The one
 *  exception is the merchant Filter pill, which UnifiedAppHeader swaps for
 *  MerchantFilterPill at render time (it needs the category/brand lists,
 *  which live in header state rather than on the action).
 */

import {
  ArrowDownUp,
  Check,
  ChevronLeft,
  ChevronsUpDown,
  ImageOff,
  Loader2,
  Package,
  Plus,
  Search,
  Settings2,
  SlidersHorizontal,
} from '@/shared/ui/icons';
import type { MobileHeaderAction } from './types';

export type MerchantSortKey = 'name' | 'code' | 'brand' | 'rate-asc' | 'rate-desc';

/** Short pill labels — the full "Sort: A–Z" phrasing is redundant once the
 *  control is a labelled pill in the header. */
const MERCHANT_SORT_LABELS: Record<MerchantSortKey, string> = {
  'name': 'A–Z',
  'code': 'Code',
  'brand': 'Brand',
  'rate-asc': 'Rate ↑',
  'rate-desc': 'Rate ↓',
};

const MERCHANT_SORT_ORDER: MerchantSortKey[] = ['name', 'code', 'brand', 'rate-asc', 'rate-desc'];

/** Rate-item list pane (LibraryItemsPane). */
export interface ResourceLibraryItemsHeaderState {
  kind: 'items';
  libraryName: string | null;
  isMaster: boolean;
  canManage: boolean;
  canRateProfile: boolean;
  canImport: boolean;
  hasSections: boolean;
  allCollapsed: boolean;
  libraryId: string | null;
}

/** Merchant catalogue pane (MerchantItemsTable).
 *
 *  Deliberately carries no `search` field: the header owns the query and the
 *  pane is a pure listener. Echoing it back here would fight the user's
 *  typing on every keystroke. The one exception flows the other way as
 *  `resource-library:clear-search`.
 *
 *  For the same reason nothing here derives from the query: a payload field
 *  that flipped per keystroke would re-render the whole header on every
 *  keypress. */
export interface ResourceLibraryMerchantHeaderState {
  kind: 'merchant';
  libraryId: string;
  /** "Travis Perkins" — names the merchant in the filter panel and aria labels. */
  merchantName: string;
  categories: { name: string; n: number }[];
  brands: { name: string; n: number }[];
  /** 'all' or a category name. */
  activeCategory: string;
  /** 'all' or a brand name. */
  activeBrand: string;
  sort: MerchantSortKey;
  showImages: boolean;
}

export type ResourceLibraryHeaderState =
  | ResourceLibraryItemsHeaderState
  | ResourceLibraryMerchantHeaderState;

export interface RateItemFormHeaderState {
  isEditing: boolean;
  isSaving: boolean;
  canSave: boolean;
  canAddResource: boolean;
  /** True when the form is a workspace lens over a Quantis Benchmark item.
   *  The Benchmark owns the build-up structure, so the workspace may only
   *  reprice its lines and the add-resource pill has nothing to offer.
   *  Required, not optional: this gate withholds an action, so a producer that
   *  forgets it must fail to compile rather than default to showing it. */
  isOverrideMode: boolean;
  libraryName: string | null;
}

export interface ResourceLibraryHeaderCtx {
  resourceLibraryHeaderState: ResourceLibraryHeaderState | null;
  rateItemFormState: RateItemFormHeaderState | null;
  isDesktopViewport: boolean;
  leadingActions: MobileHeaderAction[];
  trailingActions: MobileHeaderAction[];
  pushNotificationsAction: () => void;
}

/** Appends Resource Library pills to the caller's action arrays.
 *  Called only when `currentView === 'resource-library'`. */
export function buildResourceLibraryHeaderActions(ctx: ResourceLibraryHeaderCtx): void {
  const {
    resourceLibraryHeaderState: rlState,
    rateItemFormState,
    isDesktopViewport,
    leadingActions,
    trailingActions,
    pushNotificationsAction,
  } = ctx;

  // ── Mode 1: rate-item edit form ─────────────────────────────────────────
  // Back-chevron returns to the library, Save sits trailing, and the
  // add-resource pills span the middle.
  if (rateItemFormState) {
    leadingActions.push({
      id: 'rate-item-back',
      icon: ChevronLeft,
      ariaLabel: rateItemFormState.libraryName ?? 'Back to library',
      onClick: () => window.dispatchEvent(new CustomEvent('resource-library-item:back')),
    });
    if (rateItemFormState.canAddResource && !rateItemFormState.isOverrideMode) {
      const addCategories: Array<{ id: 'Labour' | 'Plant' | 'Material' | 'Subcontractor'; label: string }> = [
        { id: 'Labour',        label: 'Labour' },
        { id: 'Plant',         label: 'Plant' },
        { id: 'Material',      label: 'Material' },
        { id: 'Subcontractor', label: 'Subcontractor' },
      ];
      trailingActions.push({
        id: 'rate-item-add-resource',
        icon: Plus,
        ariaLabel: 'Add resource',
        label: 'Resource',
        group: 'create',
        subActions: addCategories.map((c) => ({
          id: `rate-item-add-${c.id}`,
          label: c.label,
          onClick: () => window.dispatchEvent(
            new CustomEvent('resource-library-item:add-resource', { detail: { category: c.id } })
          ),
        })),
      });
    }
    trailingActions.push({
      id: 'rate-item-save',
      icon: rateItemFormState.isSaving ? Loader2 : undefined,
      label: rateItemFormState.isSaving
        ? 'Saving'
        : rateItemFormState.isEditing ? 'Save' : 'Create',
      ariaLabel: rateItemFormState.isSaving
        ? 'Saving rate item'
        : rateItemFormState.isEditing ? 'Save changes' : 'Create rate item',
      disabled: !rateItemFormState.canSave,
      onClick: () => window.dispatchEvent(new CustomEvent('resource-library-item:save')),
      group: 'page',
    });
    pushNotificationsAction();
    return;
  }

  // ── Mode 2: merchant catalogue ──────────────────────────────────────────
  if (rlState?.kind === 'merchant') {
    const activeFilterCount =
      (rlState.activeCategory !== 'all' ? 1 : 0) + (rlState.activeBrand !== 'all' ? 1 : 0);

    leadingActions.push({
      id: 'resource-library-search',
      icon: Search,
      ariaLabel: `Search ${rlState.merchantName} products`,
      onClick: () => window.dispatchEvent(new CustomEvent('resource-library:toggle-search')),
    });

    // Category + brand are long lists (dozens of categories, 100 brands) so
    // they can't be flat subActions. UnifiedAppHeader swaps this action for
    // MerchantFilterPill, a panel of two native selects. That swap is an
    // id-intercept in renderLeadingAction, so this id also has to appear in
    // the trailing cluster's intercept allowlist now that it lives here.
    trailingActions.push({
      id: 'resource-library-merchant-filter',
      icon: SlidersHorizontal,
      ariaLabel: 'Filter by category and brand',
      badge: activeFilterCount,
      group: 'page',
    });

    trailingActions.push({
      id: 'resource-library-merchant-sort',
      icon: ArrowDownUp,
      label: MERCHANT_SORT_LABELS[rlState.sort],
      ariaLabel: `Sort: ${MERCHANT_SORT_LABELS[rlState.sort]}`,
      subActions: MERCHANT_SORT_ORDER.map((key) => ({
        id: `rl-merchant-sort-${key}`,
        label: MERCHANT_SORT_LABELS[key],
        active: rlState.sort === key,
        trailingIcon: rlState.sort === key ? Check : undefined,
        onClick: () => window.dispatchEvent(
          new CustomEvent('resource-library:set-sort', { detail: { sort: key } })
        ),
      })),
      group: 'page',
    });

    // Thumbnails are the strongest signal in a compact row, so mobile keeps
    // them on unconditionally and doesn't spend a pill on the toggle.
    if (isDesktopViewport) {
      trailingActions.push({
        id: 'resource-library-merchant-images',
        icon: rlState.showImages ? ImageOff : Package,
        ariaLabel: rlState.showImages ? 'Hide product images' : 'Show product images',
        onClick: () => window.dispatchEvent(new CustomEvent('resource-library:toggle-images')),
        group: 'view',
      });
    }

    pushNotificationsAction();
    return;
  }

  // ── Mode 3: rate-item list ──────────────────────────────────────────────
  // No library selected → only the notification action.
  if (!rlState?.libraryName) {
    pushNotificationsAction();
    return;
  }
  leadingActions.push({
    id: 'resource-library-search',
    icon: Search,
    ariaLabel: 'Search rate items',
    onClick: () => window.dispatchEvent(new CustomEvent('resource-library:toggle-search')),
  });
  // Resource Library is classified with NRM2. The former SMM7/NRM2 switcher
  // was removed so the hierarchy is stable for estimators and workspace users.
  if (rlState.hasSections) {
    trailingActions.push({
      id: 'resource-library-collapse',
      icon: ChevronsUpDown,
      ariaLabel: rlState.allCollapsed ? 'Expand all' : 'Collapse all',
      onClick: () => window.dispatchEvent(new CustomEvent('resource-library:toggle-all-sections')),
      group: 'view',
    });
  }
  if (rlState.canRateProfile) {
    trailingActions.push({
      id: 'resource-library-rate-profile',
      icon: Settings2,
      ariaLabel: 'Rate Profile',
      onClick: () => window.dispatchEvent(new CustomEvent('resource-library:open-rate-profile')),
      group: 'page',
    });
  }
  pushNotificationsAction();
}
